import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const PERSONA_STYLE = {
  REAL_ESTATE: { bg:'rgba(59,130,246,0.12)', color:'#60a5fa', icon:'🏠' },
  ROBOTICS:    { bg:'rgba(168,85,247,0.12)', color:'#c084fc', icon:'🤖' },
  FAMILY:      { bg:'rgba(251,146,60,0.12)', color:'#fb923c', icon:'👨‍👩‍👧' },
  '':          { bg:'rgba(255,255,255,0.06)', color:'#8899aa', icon:'📄' },
}

function TokenCard({ token, onClick }) {
  const ps = PERSONA_STYLE[token.persona] || PERSONA_STYLE['']
  return (
    <motion.div layout onClick={onClick}
      className="glass-card p-4 mb-3 cursor-pointer active:scale-[0.98] transition-transform"
      whileTap={{scale:0.98}}>
      <div className="flex items-center gap-3 mb-3">
        <div className="text-2xl flex-shrink-0">{ps.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[15px] truncate">{token.merchant}</div>
          <div className="text-xs text-white/30">{token.date} · {token.items.length} items</div>
        </div>
        <div className="text-lg font-black text-[#00ff87]">${parseFloat(token.total).toFixed(2)}</div>
      </div>
      {/* Mini items preview */}
      <div className="space-y-1 mb-3">
        {token.items.slice(0,3).map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="font-mono text-white/25 flex-shrink-0">{item.sku||'—'}</span>
            <span className="text-white/40 flex-1 truncate">{item.description}</span>
            <span className="font-semibold text-white/50 flex-shrink-0">${item.total.toFixed(2)}</span>
          </div>
        ))}
        {token.items.length > 3 && (
          <div className="text-[10px] text-white/20">+{token.items.length-3} more items</div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span className="tot-badge">Token of Truth ✓</span>
        <span className="font-mono text-[10px] text-white/20 truncate ml-2 max-w-[140px]">{token.bankTxId}</span>
      </div>
    </motion.div>
  )
}

function TokenDetail({ token, onClose }) {
  const ps = PERSONA_STYLE[token.persona] || PERSONA_STYLE['']
  return (
    <motion.div className="fixed inset-0 z-50 flex items-end max-w-[430px] mx-auto"
      initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose}/>
      <motion.div className="relative w-full bg-[#0e1420] rounded-t-3xl border-t border-white/[0.08] p-5 max-h-[85vh] overflow-y-auto"
        initial={{y:'100%'}} animate={{y:0}} exit={{y:'100%'}}
        transition={{type:'spring',stiffness:380,damping:38}} style={{scrollbarWidth:'none'}}>
        <div className="w-10 h-1 bg-white/10 rounded-full mx-auto mb-5"/>
        <div className="text-xl font-black mb-1">{token.merchant}</div>
        <div className="flex items-center gap-2 mb-4">
          <span className="tot-badge">VERIFIED_TRUTH ✓</span>
          {token.persona && (
            <span className="persona-tag text-[10px]"
              style={{background:ps.bg, color:ps.color}}>
              {ps.icon} {token.persona.replace('_',' ')}
            </span>
          )}
        </div>

        <div className="text-4xl font-black text-[#00ff87] mb-4" style={{textShadow:'0 0 30px rgba(0,255,135,0.35)'}}>
          ${parseFloat(token.total).toFixed(2)}
        </div>

        {/* All items */}
        <div className="text-[11px] font-bold tracking-widest uppercase text-white/30 mb-2">
          {token.items.length} Line Items
        </div>
        <div className="glass-card p-0 mb-4 overflow-hidden">
          {token.items.map((item, i) => (
            <div key={i} className={`flex items-start justify-between gap-3 p-4 ${i<token.items.length-1?'border-b border-white/[0.04]':''}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold leading-snug">{item.description}</div>
                <div className="sku-mono">SKU {item.sku||'—'} · qty {item.qty} · @${item.unit.toFixed(2)}/ea</div>
              </div>
              <div className="text-[15px] font-bold text-[#00ff87]">${item.total.toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="glass-card p-4 mb-4">
          <div className="detail-row"><span className="text-white/40 text-sm">TX ID</span><span className="font-mono text-xs text-right break-all">{token.id}</span></div>
          <div className="detail-row"><span className="text-white/40 text-sm">Bank TX / FedNow</span><span className="font-mono text-xs text-[#00ff87] text-right">{token.bankTxId}</span></div>
          <div className="detail-row"><span className="text-white/40 text-sm">Date</span><span className="text-sm text-right">{token.date}</span></div>
        </div>

        <button onClick={onClose} className="btn-ghost">Close</button>
      </motion.div>
    </motion.div>
  )
}

const FILTERS = ['ALL', 'REAL_ESTATE', 'ROBOTICS', 'FAMILY']

export default function SmartLedger({ tokens, loading, sessionError, onRefresh }) {
  const [filter,   setFilter]  = useState('ALL')
  const [selected, setSelected] = useState(null)

  const filtered = tokens.filter(t => filter === 'ALL' || t.persona === filter)
  const total    = filtered.reduce((s,t) => s + parseFloat(t.total||0), 0)

  return (
    <div className="min-h-full bg-[#080c10]">
      <div className="sticky top-0 z-10 px-5 pt-5 pb-2 bg-gradient-to-b from-[#080c10] to-transparent">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-black">🗂️ Smart Ledger</h1>
          <div className="text-right">
            <div className="text-lg font-black text-[#00ff87]">${total.toFixed(2)}</div>
            <div className="text-[10px] text-white/30">{filtered.length} tokens</div>
          </div>
        </div>
        {sessionError && <p className="text-xs text-amber-400/80 mb-2">{sessionError}</p>}
        {onRefresh && (
          <button type="button" onClick={onRefresh} className="text-[10px] font-bold text-[#00ff87] mb-2 uppercase tracking-wider">
            {loading ? 'Refreshing…' : '↻ Refresh ledger'}
          </button>
        )}
        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1" style={{scrollbarWidth:'none'}}>
          {FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-[11px] font-bold tracking-wider uppercase transition-all border ${
                filter===f ? 'bg-[rgba(0,255,135,0.12)] text-[#00ff87] border-[rgba(0,255,135,0.3)]'
                           : 'glass-card text-white/30 hover:text-white/60'}`}>
              {f.replace('_',' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 pt-2">
        <AnimatePresence>
          {loading ? (
            <div className="flex flex-col items-center pt-16 text-white/30">
              <motion.div className="w-8 h-8 border-2 border-[#00ff87] border-t-transparent rounded-full"
                animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }} />
              <p className="text-sm mt-4">Loading ledger…</p>
            </div>
          ) : filtered.length > 0 ? filtered.map(token => (
            <TokenCard key={token.id} token={token} onClick={() => setSelected(token)}/>
          )) : (
            <div className="flex flex-col items-center pt-16 text-white/20">
              <div className="text-5xl mb-3 opacity-40">📭</div>
              <p className="text-sm text-center">No tokens for this filter yet.<br/>Suck a receipt to begin.</p>
            </div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {selected && <TokenDetail token={selected} onClose={() => setSelected(null)}/>}
      </AnimatePresence>
    </div>
  )
}
